import type { Action } from 'state/actions';
import { dateNow } from 'utils/core/now';

import type { DateFilter } from '../common/blotter.model';
import { onItemsPerPageChanged, onPageChanged } from '../common/blotter.reducer.utils';
import { toLocaleDate, toLocaleDateOnlyString } from '../common/blotter.utils';
import type {
  PriceImprovementBlotterColumnStateChanged,
  PriceImprovementBlotterFilterChanged,
  PriceImprovementBlotterItemsReceived,
  PriceImprovementBlotterSelectionChanged,
} from './priceImprovementBlotter.action';
import type { PriceImprovementBlotterState } from './priceImprovementBlotter.model';
import { initialPriceImprovementBlotterState } from './priceImprovementBlotter.model';

export function priceImprovementBlotterReducer(
  state: PriceImprovementBlotterState = initialPriceImprovementBlotterState,
  action: Action,
): PriceImprovementBlotterState {
  switch (action.type) {
    case 'PRICE_IMPROVEMENT_BLOTTER_PAGE_CHANGED':
      return onPageChanged(state, action);
    case 'PRICE_IMPROVEMENT_BLOTTER_ITEMS_PER_PAGE_CHANGED':
      return onItemsPerPageChanged(state, action);
    case 'PRICE_IMPROVEMENT_BLOTTER_COLUMN_STATE_CHANGED':
      return onColumnStateChanged(state, action);
    case 'PRICE_IMPROVEMENT_BLOTTER_PREFERENCE_STATE_GET':
      return { ...state, areBlotterPreferencesInError: false };
    case 'PRICE_IMPROVEMENT_BLOTTER_PREFERENCE_STATE_LOADED':
      return {
        ...state,
        areBlotterPreferencesInError: false,
        canSaveBlotterPreferences: true,

        blotterPreferenceState: { ...state.blotterPreferenceState, ...action.blotterPreferenceState },
      };
    case 'PRICE_IMPROVEMENT_BLOTTER_PREFERENCE_STATE_LOADING_FAILED':
      return { ...state, areBlotterPreferencesInError: true, canSaveBlotterPreferences: false };
    case 'PRICE_IMPROVEMENT_BLOTTER_COLUMN_DEFINITION_RECEIVED':
      return { ...state, columnDefinition: action.columnDefinition };
    case 'PRICE_IMPROVEMENT_BLOTTER_ITEMS_REQUESTED':
      return { ...state, error: false, loading: true };
    case 'PRICE_IMPROVEMENT_BLOTTER_ITEMS_RECEIVED':
      return onPriceImprovementBlotterItemsReceived(state, action);
    case 'PRICE_IMPROVEMENT_BLOTTER_ITEMS_FETCHING_FAILED':
      return { ...state, error: true, loading: false };
    case 'PRICE_IMPROVEMENT_BLOTTER_SELECTION_CHANGED':
      return onPriceImprovementBlotterItemSelectionChanged(state, action);
    case 'PRICE_IMPROVEMENT_BLOTTER_FILTER_CHANGED':
      return onFilterChanged(state, action);
    case 'PRICE_IMPROVEMENT_BLOTTER_ADVANCED_FILTERS_TOGGLED':
      return onAdvancedFilterToggled(state);
    case 'PRICE_IMPROVEMENT_BLOTTER_FILTERS_LOADED':
      return { ...state, ...action.filters };
    case 'PRICE_IMPROVEMENT_BLOTTER_DETAIL_OPENED': {
      return {
        ...state,
        detail: {
          item: action.item,
        },
      };
    }
    case 'PRICE_IMPROVEMENT_SENSITIVITY_RECEIVED': {
      return {
        ...state,
        detail: {
          ...state.detail!,
          sensitivity: action.sensitivity,
        },
      };
    }
    case 'PRICE_IMPROVEMENT_SENSITIVITY_FAILED': {
      return {
        ...state,
        detail: {
          ...state.detail!,
          sensitivityError: action.error,
        },
      };
    }
    case 'PRICE_IMPROVEMENT_BLOTTER_DETAIL_CLOSED': {
      return {
        ...state,
        detail: undefined,
      };
    }
    case 'PRICE_IMPROVEMENT_BLOTTER_ACKNOWLEDGE_ERROR_DISMISSED':
    case 'PRICE_IMPROVEMENT_BLOTTER_ACKNOWLEDGE_REQUESTED': {
      return {
        ...state,
        acknowledgementError: undefined,
      };
    }
    case 'PRICE_IMPROVEMENT_BLOTTER_ACKNOWLEDGE_FAILED': {
      return {
        ...state,
        acknowledgementError: action.error,
      };
    }
    default:
      return state;
  }
}

function onColumnStateChanged(
  state: PriceImprovementBlotterState,
  { columnState, columnGroupState }: PriceImprovementBlotterColumnStateChanged,
): PriceImprovementBlotterState {
  return { ...state, blotterPreferenceState: { ...state.blotterPreferenceState, columnState, columnGroupState } };
}

function onPriceImprovementBlotterItemsReceived(
  state: PriceImprovementBlotterState,
  { items, totalItems, totalPages }: PriceImprovementBlotterItemsReceived,
): PriceImprovementBlotterState {
  const previousDetailItem = state.detail?.item;
  const updatedDetailItem = previousDetailItem ? items.find(item => item.id === previousDetailItem.id) : undefined;

  return {
    ...state,
    error: false,
    loading: false,
    items,
    selectedRowIds: state.selectedRowIds.filter(selectedItem => items.some(item => item.id === selectedItem)),
    totalItems,
    totalPages,
    detail: updatedDetailItem
      ? {
          ...state.detail,
          item: updatedDetailItem,
        }
      : undefined,
  };
}

function onPriceImprovementBlotterItemSelectionChanged(
  state: PriceImprovementBlotterState,
  { selectedRowIds }: PriceImprovementBlotterSelectionChanged,
): PriceImprovementBlotterState {
  return {
    ...state,
    selectedRowIds,
    acknowledgementError: undefined,
  };
}

function onFilterChanged(
  state: PriceImprovementBlotterState,
  { filter, value }: PriceImprovementBlotterFilterChanged,
): PriceImprovementBlotterState {
  switch (filter) {
    case 'dateFilter': {
      const dateFilter = <DateFilter>value;
      return {
        ...state,
        dateFilter,
        startDate: undefined,
        endDate: undefined,
        page: 1,
      };
    }
    case 'startDate': {
      const startDate = value as string;
      const endDate = state.endDate || toLocaleDateOnlyString(new Date(dateNow())); // yyyy-MM-dd

      return { ...state, startDate, endDate, page: 1 };
    }
    case 'endDate': {
      const endDate = value as string;
      const startDate = state.startDate || toLocaleDateOnlyString(toLocaleDate(state.dateFilter)); // yyyy-MM-dd

      return { ...state, startDate, endDate, page: 1 };
    }
    default:
      return { ...state, [filter]: value, page: 1 };
  }
}

function onAdvancedFilterToggled(state: PriceImprovementBlotterState): PriceImprovementBlotterState {
  return { ...state, advancedFilters: !state.advancedFilters };
}
